/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
// v77.74 — Public document-verification page.
//
// Solicitors, mediators or anyone holding a CoParent Console export
// can scan the QR code (which deep-links here as `/verify?fp=…`) or
// type a fingerprint manually. We call `/api/exports/verify_data/{fp}`
// and render a calm, branded verdict card.
//
// Public — no auth required. Mounted in `App.js` outside `/app/*` so
// it never appears in the authenticated app shell.

import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import LegalFooterInfo from "@/components/LegalFooterInfo";
import {
  Fingerprint,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Calendar,
  User,
  FileText,
  Users,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fingerprints are 16 uppercase hex chars (8 bytes of SHA-256). Accept
// lower-case + spaces / dashes when typed by hand and normalise.
const FP_RE = /^[A-F0-9]{16}$/;

const normaliseFp = (raw) =>
  (raw || "")
    .replace(/[\s·\-_]/g, "")
    .trim()
    .toUpperCase();

const formatFpDisplay = (fp) =>
  fp ? fp.match(/.{1,4}/g).join(" · ") : "";

// Render an ISO timestamp in a readable form, falling back to the raw
// value rather than crashing on unexpected input.
const formatTs = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-IE", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

// ---------- Status cards ----------------------------------------------------

const VerdictAuthentic = ({ fp, data }) => (
  <div
    className="border-2 border-[#1F9FA4] bg-gradient-to-br from-[#E0F2F2] via-white to-white dark:from-[#0F2F30]/50 dark:via-[#161E21] dark:to-[#161E21] rounded-2xl p-6 sm:p-8 shadow-[0_24px_48px_-24px_rgba(31,159,164,0.35)]"
    data-testid="verify-result-authentic"
  >
    <div className="flex items-start gap-4">
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#1F9FA4] text-white inline-flex items-center justify-center">
        <CheckCircle2 className="w-7 h-7" strokeWidth={2.2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0B4D50] dark:text-[#3FB8BC]">
          Verified
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[#0B4D50] dark:text-[#F4F8F9] mt-1">
          Document is authentic
        </h2>
        <p className="text-sm text-[#0B4D50]/80 dark:text-[#A8B6BB] mt-2">
          This bundle was issued through CoParent Console and the
          fingerprint matches the original record.
        </p>
      </div>
    </div>

    <div className="mt-6 pt-6 border-t border-[#A7D8DA] dark:border-[#1F9FA4]/30 grid sm:grid-cols-2 gap-x-6 gap-y-4">
      <Field icon={User} label="Issued by">
        <div className="text-[#1A2225] dark:text-[#F4F8F9] font-medium">{data.exported_by_name || "—"}</div>
        {data.exported_by_email && (
          <div className="text-xs text-[#48595E] dark:text-[#A8B6BB] mt-0.5 truncate">{data.exported_by_email}</div>
        )}
      </Field>
      <Field icon={Calendar} label="Exported at">
        <div className="text-[#1A2225] dark:text-[#F4F8F9]">{formatTs(data.exported_at)}</div>
      </Field>
      <Field icon={Calendar} label="Date range covered">
        <div className="text-[#1A2225] dark:text-[#F4F8F9]">
          {formatDate(data.start_date)} <span className="text-[#6F828A]">→</span> {formatDate(data.end_date)}
        </div>
      </Field>
      <Field icon={Users} label="Children covered">
        <div className="text-[#1A2225] dark:text-[#F4F8F9]">
          {(data.child_names || []).join(", ") || "—"}
        </div>
      </Field>
      <Field icon={FileText} label="Sections" wide>
        <div className="text-[#1A2225] dark:text-[#F4F8F9] capitalize">
          {(data.sections || []).join(", ") || "—"}
        </div>
      </Field>
    </div>

    {data.counts && Object.keys(data.counts).length > 0 && (
      <div className="mt-6 pt-6 border-t border-[#A7D8DA] dark:border-[#1F9FA4]/30">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0B4D50] dark:text-[#3FB8BC]">
          Section counts at export time
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="verify-counts">
          {Object.entries(data.counts).map(([k, v]) => (
            <div
              key={k}
              className="px-3 py-2 rounded-md bg-white dark:bg-[#0F2F30]/40 border border-[#A7D8DA] dark:border-[#1F9FA4]/30 text-center"
            >
              <div className="font-mono-data tabular-nums text-lg font-semibold text-[#0B4D50] dark:text-[#3FB8BC]">{v}</div>
              <div className="text-[11px] uppercase tracking-wider text-[#48595E] dark:text-[#A8B6BB] mt-0.5 truncate">{k}</div>
            </div>
          ))}
        </div>
      </div>
    )}

    <p className="mt-6 pt-4 border-t border-[#A7D8DA] dark:border-[#1F9FA4]/30 text-xs text-[#48595E] dark:text-[#A8B6BB] leading-relaxed">
      Verification confirms the bundle was issued by an authenticated user
      through this platform. It does <em>not</em> vouch for the truthfulness
      of any specific entry — courts should evaluate the underlying content
      on the merits.
    </p>
  </div>
);

const VerdictAltered = ({ fp, reason }) => {
  const tampered = reason === "not_found";
  const titleText = tampered ? "Document could not be verified" : "Invalid fingerprint";
  const subText = tampered
    ? "No bundle was found for this fingerprint. The PDF may have been altered, or the bundle may have been generated on a different deployment."
    : "The fingerprint format is invalid. A genuine CoParent Console fingerprint is exactly 16 hexadecimal characters.";
  return (
    <div
      className="border-2 border-[#C0291D] bg-gradient-to-br from-[#FBEFED] via-white to-white dark:from-[#3A1A18]/50 dark:via-[#161E21] dark:to-[#161E21] rounded-2xl p-6 sm:p-8 shadow-[0_24px_48px_-24px_rgba(192,41,29,0.25)]"
      data-testid={tampered ? "verify-result-not-found" : "verify-result-invalid"}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#C0291D] text-white inline-flex items-center justify-center">
          {tampered
            ? <XCircle className="w-7 h-7" strokeWidth={2.2} />
            : <AlertTriangle className="w-7 h-7" strokeWidth={2.2} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C2A22] dark:text-[#FFB6AE]">
            {tampered ? "Not verified" : "Invalid"}
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-[#8C2A22] dark:text-[#F4F8F9] mt-1">
            {titleText}
          </h2>
          <p className="text-sm text-[#8C2A22]/80 dark:text-[#FFB6AE] mt-2">
            {subText}
          </p>
        </div>
      </div>
      {fp && (
        <div className="mt-6 pt-6 border-t border-[#E8B8B2] dark:border-[#7A2A22]">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C2A22] dark:text-[#FFB6AE]">
            Submitted fingerprint
          </div>
          <div className="mt-2 font-mono-data text-sm text-[#1A2225] dark:text-[#F4F8F9] break-all" data-testid="verify-submitted-fp">
            {formatFpDisplay(fp) || fp}
          </div>
        </div>
      )}
      <p className="mt-6 pt-4 border-t border-[#E8B8B2] dark:border-[#7A2A22] text-xs text-[#48595E] dark:text-[#A8B6BB] leading-relaxed">
        If you received this PDF directly from a CoParent Console user, ask
        them to re-export the bundle and share the fresh copy. The fingerprint
        is generated at export time and uniquely identifies the file.
      </p>
    </div>
  );
};

const Field = ({ icon: Icon, label, wide = false, children }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] font-semibold text-[#6F828A] dark:text-[#7A8A91]">
      <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
      {label}
    </div>
    <div className="mt-1.5 text-sm">{children}</div>
  </div>
);

// ---------- Page -----------------------------------------------------------

export default function Verify() {
  const [params, setParams] = useSearchParams();
  const initialFp = normaliseFp(params.get("fp") || "");
  const [input, setInput] = useState(initialFp);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { fp, data | reason }

  const runVerify = useCallback(async (rawFp) => {
    const fp = normaliseFp(rawFp);
    setError(null);
    setResult(null);
    if (!fp) {
      setError("Please enter a fingerprint.");
      return;
    }
    if (!FP_RE.test(fp)) {
      // Render the invalid card directly without hitting the API — saves
      // a roundtrip, and the API would echo the same invalid_format
      // reason anyway.
      setResult({ fp, reason: "invalid_format" });
      setParams({ fp });
      return;
    }
    setBusy(true);
    try {
      const r = await axios.get(`${API_BASE}/exports/verify_data/${fp}`);
      const body = r.data || {};
      if (body.found) {
        setResult({ fp, data: body });
      } else {
        setResult({ fp, reason: body.reason || "not_found" });
      }
      setParams({ fp });
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "We couldn't reach the verification service. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }, [setParams]);

  // Auto-run on mount if `?fp=` came in via the QR code. Intentionally
  // fires exactly once on mount — adding `runVerify` to the deps would
  // re-fire whenever the callback identity changed (it doesn't here,
  // but lint can't prove that), and adding `initialFp` would re-fire
  // every time the user typed and submitted.
  //
  // `set-state-in-effect` is suppressed because the side-effect
  // (calling /api/exports/verify_data/...) is exactly what we want
  // here — this is a network-fetch effect, not a render-time
  // derivation, and React 19's new rule is over-eager for the
  // fire-once-on-mount pattern. Documented React community workaround
  // until `useEffectEvent` ships stable.
  useEffect(() => {
    if (initialFp) {
      runVerify(initialFp);
    }
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    runVerify(input);
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setInput("");
    setParams({});
  };

  return (
    <div
      className="min-h-screen bg-white dark:bg-[#0E1416] text-[#1A2225] dark:text-[#E8EEF0]"
      data-testid="verify-page"
    >
      {/* HEADER */}
      <header className="sticky top-0 z-30 border-b border-[#E5EBEC] dark:border-[#1F2A2D] bg-white/85 dark:bg-[#0E1416]/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="verify-brand">
            <Logo size={32} />
            <span className="font-heading font-semibold text-base sm:text-lg tracking-tight text-[#1A2225] dark:text-[#F4F8F9]">
              CoParent Console
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] inline-flex items-center gap-1.5"
            data-testid="verify-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Home</span>
          </Link>
        </div>
      </header>

      {/* HERO + form */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#E0F2F2]/60 via-white to-white dark:from-[#0F2F30]/70 dark:via-[#0E1416] dark:to-[#0E1416]"
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-12 sm:pb-16">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1F9FA4] dark:text-[#3FB8BC]">
            Document verification
          </div>
          <h1 className="font-heading text-[2rem] leading-[1.15] sm:text-5xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
            Verify a court bundle
          </h1>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
            Every CoParent Console export carries a unique cryptographic
            fingerprint. Scan the QR code on the document, or paste the
            fingerprint below, to confirm the file is authentic and unaltered.
          </p>

          <form onSubmit={onSubmit} className="mt-8" data-testid="verify-form">
            <label htmlFor="verify-input" className="text-sm font-medium text-[#1A2225] dark:text-[#F4F8F9] flex items-center gap-2">
              <Fingerprint className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.8} />
              Document fingerprint
            </label>
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <Input
                id="verify-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="e.g. 9F3A1C47B820E0D4"
                className="font-mono-data tracking-wider min-h-[44px] bg-white dark:bg-[#161E21]"
                autoComplete="off"
                spellCheck={false}
                data-testid="verify-input"
              />
              <Button
                type="submit"
                disabled={busy}
                className="bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-11 px-6"
                data-testid="verify-submit"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking…
                  </>
                ) : (
                  <>
                    Verify
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
            <p className="mt-2 text-xs text-[#6F828A] dark:text-[#7A8A91]">
              16 hexadecimal characters. Spaces, dashes and dots are ignored.
            </p>
            {error && (
              <div className="mt-4 flex items-start gap-3 p-3 rounded-md bg-[#FBEFED] dark:bg-[#3A1A18] border border-[#E8B8B2] dark:border-[#7A2A22] text-[#8C2A22] dark:text-[#FFB6AE] text-sm" data-testid="verify-error">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                <span>{error}</span>
              </div>
            )}
          </form>
        </div>
      </section>

      {/* RESULT */}
      {result && (
        <section className="bg-[#FAFCFC] dark:bg-[#0B1011] border-y border-[#E5EBEC] dark:border-[#1F2A2D]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14">
            {result.data
              ? <VerdictAuthentic fp={result.fp} data={result.data} />
              : <VerdictAltered fp={result.fp} reason={result.reason} />}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={reset}
                variant="outline"
                className="rounded-md border-[#D6F0EF] dark:border-[#2A3539]"
                data-testid="verify-check-another"
              >
                Verify another document
              </Button>
              <Link to="/about">
                <Button variant="ghost" className="rounded-md text-[#116B6E] dark:text-[#3FB8BC]">
                  Learn about CoParent Console
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* WHAT THIS MEANS */}
      <section className="bg-white dark:bg-[#0E1416]" data-testid="verify-explainer">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1F9FA4] dark:text-[#3FB8BC]">
            What this means
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
            How verification works
          </h2>
          <div className="mt-6 space-y-4 text-sm sm:text-base text-[#48595E] dark:text-[#B6C2C7] leading-relaxed">
            <p>
              When a CoParent Console user generates a court bundle, the
              platform computes a SHA-256 hash over a deterministic snapshot
              of the records in scope. That fingerprint is printed on every
              page of the PDF and stored on our servers alongside the
              bundle&apos;s metadata.
            </p>
            <p>
              Anyone holding the PDF can come to this page, type or scan the
              fingerprint, and confirm in seconds that the file matches the
              record we issued. If the bundle has been altered — even by a
              single character — the fingerprint won&apos;t match and the
              lookup will fail.
            </p>
            <p>
              No account is needed. Only the public-safe metadata is shown —
              who issued the bundle, when, the date range, the sections
              included and the number of records per section. The contents of
              the records themselves remain private to the parties on the
              account.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#FAFCFC] dark:bg-[#0B1011]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo size={24} />
            <span className="font-heading font-semibold text-sm text-[#1A2225] dark:text-[#F4F8F9]">CoParent Console</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-[#6F828A] dark:text-[#7A8A91] flex-wrap justify-center">
            <Link to="/about" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">About</Link>
            <span aria-hidden="true">·</span>
            <Link to="/professionals" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">For professionals</Link>
            <span aria-hidden="true">·</span>
            <Link to="/help" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Help</Link>
            <span aria-hidden="true">·</span>
            <Link to="/contact" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Contact</Link>
            <span aria-hidden="true">·</span>
            <Link to="/privacy" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Privacy</Link>
          </div>
        </div>
        <LegalFooterInfo />
      </footer>
    </div>
  );
}
